import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  firstName?: string;
  lastName?: string;
  clientId?: number;
  clientName?: string;
  siteId?: number;
  siteName?: string;
  siteIds?: number[];
  canSendVehicleNotifications?: boolean;
  trainingRequired?: boolean;
}

export interface LoginResult {
  mfaRequired: boolean;
  mfaToken?: string;
  user?: User;
}

// Granular permission flags derived from a user's role.
export interface UserPermissions {
  // Backend / admin-side areas
  canViewWorkstream: boolean;
  canProcessWorkstream: boolean; // approve / reject actions
  canViewClients: boolean;
  canViewInsights: boolean;      // admin-level Insights & Reporting
  canViewUsers: boolean;
  canViewAdminTools: boolean;
  canViewConsentRecords: boolean;
  canViewClaims: boolean;
  canManageClaims: boolean;
  // Fleet-side areas
  canViewVehicles: boolean;
  canManageVehicles: boolean;    // create / edit / delete
  canViewDrivers: boolean;
  canViewSites: boolean;
  canViewMessages: boolean;
  canViewReports: boolean;       // client-level Insights & Reports
}

const NO_PERMISSIONS: UserPermissions = {
  canViewWorkstream: false, canProcessWorkstream: false,
  canViewClients: false, canViewInsights: false,
  canViewUsers: false, canViewAdminTools: false, canViewConsentRecords: false,
  canViewClaims: false, canManageClaims: false,
  canViewVehicles: false, canManageVehicles: false,
  canViewDrivers: false, canViewSites: false, canViewMessages: false, canViewReports: false,
};

/**
 * Returns the full set of permission flags for a given role string.
  * Role hierarchy (highest → lowest): SuperAdmin → SuperUser → Admin → ClientAdmin/FleetManager → MTA → Client
 */
export function buildPermissions(role: string): UserPermissions {
  switch (role) {
    // ── Tier 1: SuperAdmin – full admin access + Security & Compliance ─────────
    // Fleet data (vehicles, drivers, sites) is accessed via the Clients section,
    // not via standalone nav items. canViewVehicles etc. intentionally false here.
    case 'SuperAdmin':
      return {
        canViewWorkstream: true, canProcessWorkstream: true,
        canViewClients: true, canViewInsights: true,
        canViewUsers: true, canViewAdminTools: true, canViewConsentRecords: true,
        canViewClaims: true, canManageClaims: true,
        canViewVehicles: false, canManageVehicles: false,
        canViewDrivers: false, canViewSites: false, canViewMessages: false, canViewReports: false,
      };

    // ── Tier 2: SuperUser – full admin-side access ─────────────────────────
    case 'SuperUser':
      return {
        canViewWorkstream: true, canProcessWorkstream: true,
        canViewClients: true, canViewInsights: true,
        canViewUsers: true, canViewAdminTools: true, canViewConsentRecords: true,
        canViewClaims: true, canManageClaims: true,
        canViewVehicles: false, canManageVehicles: false,
        canViewDrivers: false, canViewSites: false, canViewMessages: false, canViewReports: false,
      };

    // ── Tier 2: Admin – all areas except Consent Records & Admin Tools ─────
    case 'Admin':
      return {
        canViewWorkstream: true, canProcessWorkstream: true,
        canViewClients: true, canViewInsights: true,
        canViewUsers: true, canViewAdminTools: false, canViewConsentRecords: false,
        canViewClaims: true, canManageClaims: true,
        canViewVehicles: false, canManageVehicles: false,
        canViewDrivers: false, canViewSites: false, canViewMessages: false, canViewReports: false,
      };

    // ── Tier 3: Fleet Manager – fleet operations + workstream + messages ─────
    case 'FleetManager':
    case 'FleetManagerAdmin':
    case 'ClientAdmin':
      return {
        canViewWorkstream: false, canProcessWorkstream: false,
        canViewClients: false, canViewInsights: false,
        canViewUsers: true, canViewAdminTools: false, canViewConsentRecords: false,
        canViewClaims: true, canManageClaims: false,
        canViewVehicles: true, canManageVehicles: true,
        canViewDrivers: true, canViewSites: true, canViewMessages: true, canViewReports: true,
      };

    // ── Tier 4: MTA – full admin access excluding Users, Admin Tools & Security
    // MTA are the primary workstream users: full approve/reject capability.
    // Excluded: Users list, Admin Tools (user activity), Security & Compliance.
    // MTA: Full workstream + clients + drivers. Vehicles accessed via All Fleets / Clients.
    // Insights shows client-side reports view (not admin insights).
    // No standalone Sites, Users, Vehicles, or Admin Insights in nav.
    case 'MTA':
      return {
        canViewWorkstream: true, canProcessWorkstream: true,
        canViewClients: true, canViewInsights: false,
        canViewUsers: false, canViewAdminTools: false, canViewConsentRecords: false,
        canViewClaims: true, canManageClaims: true,
        canViewVehicles: false, canManageVehicles: false,
        canViewDrivers: true, canViewSites: false, canViewMessages: false, canViewReports: true,
      };
    case 'Onboarder':
      return {
        canViewWorkstream: true, canProcessWorkstream: true,
        canViewClients: true, canViewInsights: false,
        canViewUsers: true, canViewAdminTools: false, canViewConsentRecords: false,
        canViewClaims: true, canManageClaims: true,
        canViewVehicles: true, canManageVehicles: true,
        canViewDrivers: true, canViewSites: false, canViewMessages: false, canViewReports: true,
      };

    case 'ClaimsManager':
      return {
        canViewWorkstream: false, canProcessWorkstream: false,
        canViewClients: false, canViewInsights: false,
        canViewUsers: false, canViewAdminTools: false, canViewConsentRecords: false,
        canViewClaims: true, canManageClaims: true,
        canViewVehicles: false, canManageVehicles: false,
        canViewDrivers: false, canViewSites: false, canViewMessages: false, canViewReports: false,
      };

    // ── Tier 5: Client – standard fleet portal user ──────────────────────────
    default:
      return {
        canViewWorkstream: false, canProcessWorkstream: false,
        canViewClients: false, canViewInsights: false,
        canViewUsers: false, canViewAdminTools: false, canViewConsentRecords: true,
        canViewClaims: true, canManageClaims: false,
        canViewVehicles: true, canManageVehicles: true,
        canViewDrivers: true, canViewSites: true, canViewMessages: true, canViewReports: true,
      };
  }
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<LoginResult>;
  verifyMfa: (mfaToken: string, code: string) => Promise<User>;
  logout: () => void;
  loading: boolean;
  // Convenience role flags
  isAdmin: boolean;      // true for Admin and SuperUser
  isSuperAdmin: boolean; // backward-compat alias for isSuperUser
  isSuperUser: boolean;
  isFleetManager: boolean;
  isMTA: boolean;
  // Granular permissions derived from role
  permissions: UserPermissions;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token on mount
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (username: string, password: string): Promise<LoginResult> => {
    try {
      const response = await authAPI.login(username, password);
      const data = response.data;

      if (data.mfaRequired) {
        return { mfaRequired: true, mfaToken: data.mfaToken };
      }

      // Fallback: direct login (shouldn't happen with MFA enabled, but safe)
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);
      return { mfaRequired: false, user: data.user };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const verifyMfa = async (mfaToken: string, code: string): Promise<User> => {
    try {
      const response = await authAPI.verifyMfa(mfaToken, code);
      // Defensively handle both camelCase and PascalCase property names
      const newToken: string = response.data.token ?? response.data.Token;
      const newUser: User = response.data.user ?? response.data.User;

      if (!newUser || !newToken) {
        throw new Error('Invalid authentication response from server. Please try again.');
      }

      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      setToken(newToken);
      setUser(newUser);
      return newUser;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Verification failed');
    }
  };

  const logout = () => {
    // Fire-and-forget: record the logout event server-side before clearing state
    authAPI.logout().catch(() => { /* non-critical */ });
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Clear all login-session keys so each new login starts with a clean state.
    // This prevents stale fleetWizardPending from re-triggering the wizard on the
    // next login, and resets the Academy modal so it can re-appear after a fresh GDPR accept.
    sessionStorage.removeItem('gdprConsent');
    sessionStorage.removeItem('fleetWizardPending');
    sessionStorage.removeItem('academyModalShown');
    setToken(null);
    setUser(null);
    window.location.href = '/login';
  };

  const isSuperUser = user?.role === 'SuperUser' || user?.role === 'SuperAdmin';
  const isSuperAdmin = isSuperUser; // backward-compat alias
  const isAdmin = user?.role === 'Admin' || isSuperUser;
  const isFleetManager = user?.role === 'FleetManager' || user?.role === 'FleetManagerAdmin' || user?.role === 'ClientAdmin';
  const isMTA = user?.role === 'MTA' || user?.role === 'Onboarder';
  const permissions = buildPermissions(user?.role ?? 'Client');

  return (
    <AuthContext.Provider value={{ user, token, login, verifyMfa, logout, loading, isAdmin, isSuperAdmin, isSuperUser, isFleetManager, isMTA, permissions }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
